import { useState } from 'react';

import { useAppContext } from '../../contexts/Context';
import SliderContainer, { Slide } from './Slider';
import Asset from './Asset';
import UniversalLinkContainer from './UniversalLink';
import CountdownTimer from './CountdownTimer';
import AssetsByScreenSize from './AssetsByScreenSize';
import {
  EyebrowImage,
  EyebrowHeader,
  CardHeader,
  CardSubHeader,
  CardCopy,
  CardRichCopy,
  CardPoints,
  CardLinks,
} from './CardContent';
import {
  getContainerStyles,
  generateCenteredColumnSpanClassNames,
} from '../../../../utils/Utils';
import { FONT_VARIANT_CLASS_NAME } from '../../../../consts/consts';
import { withCssModulesClassNames } from '../../common/nextMigrationHelpers';
// eslint-disable-next-line import/no-named-default
import { default as ComponentStyles } from '../../scss/components/Card.module.scss';
import { Color } from '../../types/common';
import {
  Asset as AssetType, Point, Link, LinkTypes,
} from './header/header.types';
import Nullable from '../../../../shared/nullable';
import StyleDto from '../../../../shared/style.dto';
import SliderAlignment from '../../types/schema/models/SliderAlignment.dto';
import useComponentContentStyles from '../../hooks/useComponentContentStyles';

const withCssModulesClassNamesHandler = withCssModulesClassNames(ComponentStyles);

/**
 * The card component is an extremely versatile component that is the
 * building block for several other components such as the Hero, Full-Screen Video
 * and Product Spotlight Components
 */
interface CardProps {
  id?: string;
  bodyFontSize?: keyof typeof FONT_VARIANT_CLASS_NAME;
  images?: AssetType[];
  styles?: StyleDto[];
  eyebrowHeader?: string;
  header?: string;
  className?: string;
  align?: SliderAlignment;
  verticalAlign?: string;
  overlayColor?: Color;
  subHeader?: string;
  copy?: string;
  richTextCopy?: {
    markdown: string;
  }
  backgroundColor?: Color;
  textColor?: Color;
  countdownTarget?: string;
  countdownHeader?: string;
  theme?: string;
  points?: Point[];
  aspectRatios?: string[];
  autoplay?: boolean;
  type?: LinkTypes;
  overlayAsset?: AssetType[];
  links?: Link[];
  disableInfiniteSlider?: boolean;
  columnWidths?: string[];
  ancestorImitatesLink?: Nullable<boolean>
  components?: {
    type: string;
    id: string;
  }[];
  icon?: AssetType;
}

const Card = (componentProps: CardProps) => {
  const {
    id,
    type,
    theme,
    align,
    autoplay,
    textColor,
    className,
    columnWidths,
    overlayColor,
    eyebrowHeader,
    backgroundColor,
    countdownTarget,
    countdownHeader,
    ancestorImitatesLink,
    disableInfiniteSlider,
    bodyFontSize = '',
    links = [],
    points = [],
    styles = [],
    images = [],
    components = [],
    aspectRatios = [],
    overlayAsset = [],
  } = componentProps;

  const [assetLoaded, setAssetLoaded] = useState(false);
  const [index, setIndex] = useState(0);
  const isPointCarousel = Boolean(typeof autoplay === 'boolean' && points?.length);
  const nonSliderCard = !isPointCarousel ? 'non-slider-card' : null;
  const isHero = id && id === components?.find((component) => component.type === 'Card')?.id;
  const hero = className?.includes('hero') ?? false;
  const { nextApp, windowWidth } = useAppContext();

  const getCardPointProp = <T extends keyof CardProps>(propName: T) => {
    const { [propName]: prop } = componentProps;
    return (
      isPointCarousel ? ((points?.[index || 0] as unknown) as CardProps)?.[propName] : prop
    ) || '';
  };

  const renderAsset = (asset: AssetType) => (
    <Asset
      asset={asset}
      maxWidth={isHero ? asset.width ?? 0 : windowWidth}
      priority={hero}
      onLoad={() => setAssetLoaded(true)}
    />
  );

  const renderOverlayAsset = (asset: AssetType) => (
      <Asset
        imageClassName="card__overlay-asset-image"
        asset={asset}
        priority={hero}
        imageProps={{ blurryPlaceholder: false }}
      />
  );

  const renderAssetsByScreenSize = (
    imagesToRender: AssetType[],
    columnWidthsClassNames: string | Record<string, never>,
  ) => (
    <AssetsByScreenSize
      assets={imagesToRender}
      hero={hero}
      multiGroupWrapperClassName={withCssModulesClassNamesHandler(
        'card__assets',
        aspectRatios,
        columnWidthsClassNames,
        nonSliderCard,
      )}
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      renderMultiGroup={renderSlideShow}
      singleGroupWrapperClassName={withCssModulesClassNamesHandler(
        'card__asset',
        aspectRatios,
        columnWidthsClassNames,
      )}
      renderSingleGroup={renderAsset}
    />
  );

  const renderLinkOverlay = () => {
    const [link] = (
      isPointCarousel && Array.isArray(points) ? points[index || 0]?.links : links
    ) || [];

    return link ? (
      <UniversalLinkContainer
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...link}
        buttonColor={null}
        color={null}
        className={withCssModulesClassNamesHandler('hero-link-overlay')}
        label={`${link.text || link.destinationPage?.url || ''}`}
      />
    ) : null;
  };

  const shouldRenderAsLink = ancestorImitatesLink && links.length > 0;
  const slideup = className && className.includes('slide-up');
  const columnWidthsClassNames = generateCenteredColumnSpanClassNames(columnWidths);
  const [link] = links;
  const isClickableHero = Boolean(
    shouldRenderAsLink
    && !overlayAsset.length
    && hero
    && link?.destinationPage?.url,
  );

  const CardContentWrapper = shouldRenderAsLink
    ? function UWrapper(props: Pick<CardProps, 'styles' | 'className'>) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <UniversalLinkContainer {...link} {...props} />;
    } : function OWrapper({
      children,
      styles: cardStyles,
      className: cardContentWrapperClassName,
    }: { children: React.ReactNode, className: string, styles: StyleDto[] }) {
      const {
        componentStyles,
        componentContainerRef,
      } = useComponentContentStyles({ stylesProp: cardStyles });

      // eslint-disable-next-line react/jsx-props-no-spreading
      return (
        <div
          ref={componentContainerRef}
          style={componentStyles}
          className={cardContentWrapperClassName}
        >
          {children}
        </div>
      );
    };

  const cardEyebrowIcon = getCardPointProp('icon');
  const cardImages = isPointCarousel ? points.map((point) => point.images).flat() : images;
  const cardHeader = getCardPointProp('header');
  const cardSubHeader = getCardPointProp('subHeader');
  const cardAlignment = getCardPointProp('align')?.toLowerCase() || 'center';
  const cardVerticalAlignment = getCardPointProp('verticalAlign')?.toLowerCase() || 'middle';
  const cardCopy = getCardPointProp('copy');
  const cardRichCopy = getCardPointProp('richTextCopy');
  const cardStyles = isPointCarousel && points[index || 0]?.styles?.length
    ? points[index || 0]?.styles : styles;

  const renderSlideShow = () => (
    <SliderContainer
      className={withCssModulesClassNamesHandler('card__slider', { nextApp })}
      theme={theme}
      slidesPerView={1}
      pagination={true}
      navStyles={getContainerStyles(null, textColor)}
      disableInfinite={disableInfiniteSlider}
      setSlideIndex={setIndex}
      onChange={setIndex}
      align={align?.toLowerCase() as SliderAlignment}
      autoplay={autoplay}
      isPointCarousel={!!isPointCarousel}
      speed={isPointCarousel ? 1200 : 300}
    >
      {cardImages.map((asset) => (
        <Slide key={asset.handle}>{renderAsset(asset)}</Slide>
      ))}
    </SliderContainer>
  );

  return (
      <div
        className={withCssModulesClassNamesHandler('card', className, {
          hero,
          slideup: slideup && assetLoaded,
        })}
        style={getContainerStyles(backgroundColor, textColor)}
      >
        {overlayColor && (
          <div
            className={withCssModulesClassNamesHandler(columnWidthsClassNames, 'card__overlay')}
            style={{
              backgroundColor: `rgba(${overlayColor.rgba.r}, ${overlayColor.rgba.g}, ${overlayColor.rgba.b}, ${overlayColor.rgba.a})`,
            }}
          />
        )}
        {renderAssetsByScreenSize(cardImages, columnWidthsClassNames)}
        {!isClickableHero ? (
        <CardContentWrapper
          styles={cardStyles}
          className={withCssModulesClassNamesHandler(
            'card__body',
            columnWidthsClassNames,
            cardAlignment,
            cardVerticalAlignment,
          )}
        >
          {isPointCarousel && cardEyebrowIcon && <EyebrowImage asset={cardEyebrowIcon} />}
          {eyebrowHeader && <EyebrowHeader>{eyebrowHeader}</EyebrowHeader>}
          {Array.isArray(overlayAsset) && overlayAsset?.length > 0 && (
            <AssetsByScreenSize
              assets={overlayAsset}
              singleGroupWrapperClassName="card__overlay-asset"
              renderSingleGroup={renderOverlayAsset}
            />
          )}
          {cardHeader && <CardHeader>{cardHeader}</CardHeader>}
          {cardSubHeader && (
            <CardSubHeader>{cardSubHeader}</CardSubHeader>
          )}
          {
            typeof cardRichCopy === 'object' && cardRichCopy.markdown ? (
              <CardRichCopy
                text={cardRichCopy}
                fontSizeClassName={FONT_VARIANT_CLASS_NAME[bodyFontSize || 'XL']}
              />
            ) : (
              <CardCopy
                text={(cardCopy ?? '').toString()}
                fontSizeClassName={FONT_VARIANT_CLASS_NAME[bodyFontSize || 'XL']}
              />
            )
          }
          {countdownTarget && (
            <h2>
              <CountdownTimer
                key={countdownTarget}
                header={countdownHeader}
                countdownTarget={countdownTarget}
              />
            </h2>
          )}
          <CardPoints points={points} isPointCarousel={isPointCarousel} />
          {(
            <CardLinks
              points={points}
              index={index}
              isPointCarousel={isPointCarousel}
              links={links}
              type={type}
              shouldRenderAsLink={shouldRenderAsLink}
              isHero
            />
          )}
        </CardContentWrapper>
        ) : null}
        {(isClickableHero || isPointCarousel) ? renderLinkOverlay() : null}
      </div>
  );
};

export default Card;
